import { getQueryStringParameter, hasQueryStringParameter, removeQuery } from 'owa-querystring';
import { logStartUsage } from 'owa-analytics-start';
import BookWithMeView from 'bookwithme-main-module/lib/schema/BookWithMeView';
import type BookWithMeUrlData from 'bookwithme-main-module/lib/schema/BookWithMeUrlData';
import {
    lazyOpenBookingDashboard,
    lazyOpenBookingCompose,
    lazyEditBookingMeeting,
    lazyOpenBookingPage,
    lazyOpenMeetingTypePage,
    lazyOpenMeetingDetailsPage,
} from 'bookwithme-routes';
import {
    ORGANIZER_PAGE_KEYWORD,
    MEETING_TYPE_ROUTE_KEYWORD,
    ORGANIZER_NEW_MEETING_TYPE_ROUTE_KEYWORD,
    ATTENDEE_PAGE_KEYWORD,
    ATTENDEE_MEETING_DETAILS_ROUTE_KEYWORD,
} from 'bookwithme-shared/lib/store/schema/BookWithMeRouteConstants';
import { DEFAULT_ENTRYPOINT } from 'bookwithme-shared/lib/store/schema/LinkEntryPoint';
import {
    DEFAULT_MEETING_TITLE,
    DEFAULT_MEETING_DURATION,
} from 'bookwithme-shared/lib/store/schema/LinkMeetingDetails';
import { lazySetAttendeeEntryPoint } from 'bookwithme-attendee';
import {
    lazySetOrganizerEntryPoint,
    lazySetOrganizerMeetingDetails,
    lazySetShowServiceOutageBanner,
} from 'bookwithme-organizer';

function getValidatedGetRequestUrlParam(): string {
    const getRequestUrl = getQueryStringParameter('getrequesturl');
    if (getRequestUrl) {
        logStartUsage('MeetGetRequestUrlFound');
        let decodedGetRequestUrl = decodeURIComponent(getRequestUrl);
        if (decodedGetRequestUrl !== getRequestUrl) {
            // The original value of getRequestUrl has been decoded once already
            logStartUsage('MeetGetRequestUrlEncoded', { isEncodedTwice: true });

            // Try decode again and stop even if we don't know if it's still being encoded
            const newDecodedGetRequestUrl = decodeURIComponent(decodedGetRequestUrl);
            if (newDecodedGetRequestUrl !== decodedGetRequestUrl) {
                logStartUsage('MeetGetRequestUrlEncoded', { isEncodedThreeTimes: true });
                decodedGetRequestUrl = newDecodedGetRequestUrl;
            }
        }
        return decodedGetRequestUrl;
    }
    return getRequestUrl;
}

export function parseURL(): BookWithMeUrlData {
    let getBookingMailbox = '';
    let getBookingId = '';
    let getMeetingTypeId = '';
    let isAttendeeView = false;

    const validatedGetRequestUrl = getValidatedGetRequestUrlParam();

    const windowLocation = window.location;
    const urlPathname = windowLocation.pathname.split('/');

    // Get entry point from URL
    const entryPoint = getQueryStringParameter('ep') ?? DEFAULT_ENTRYPOINT;
    // Get meeting details from URL
    const meetingTitle = getQueryStringParameter('title') ?? DEFAULT_MEETING_TITLE;
    const meetingDuration =
        parseInt(getQueryStringParameter('duration')) ?? DEFAULT_MEETING_DURATION;
    // If redirect from old findtime url, get 'from' qsp. Ex. outlook.office.com/bookwithme/me?from=1
    const redirectFromOldUrl = +(getQueryStringParameter('from') ?? 0);
    const newSearch = removeQuery(location.search, 'ep');
    history.replaceState(history.state, '/', location.pathname + newSearch);

    if (urlPathname.length > 1) {
        const viewPage = urlPathname[2];
        switch (viewPage) {
            case ATTENDEE_PAGE_KEYWORD:
                isAttendeeView = true;
                lazySetAttendeeEntryPoint.importAndExecute(entryPoint);
                getBookingMailbox = urlPathname.length > 2 ? urlPathname[3] : '';
                // TODO: figure out root cause of encode '%40' to '%2540', which cause runtime error
                getBookingMailbox = decodeMailboxIfNeeded(getBookingMailbox);
                if (urlPathname.length > 4) {
                    const detailType = urlPathname[4];
                    const detailTypeId = urlPathname[5];
                    switch (detailType) {
                        case MEETING_TYPE_ROUTE_KEYWORD:
                            getMeetingTypeId = detailTypeId;
                            lazyOpenMeetingTypePage.importAndExecute(
                                getBookingMailbox,
                                getMeetingTypeId,
                                entryPoint
                            );
                            break;
                        case ATTENDEE_MEETING_DETAILS_ROUTE_KEYWORD:
                            getBookingId = detailTypeId;
                            lazyOpenMeetingDetailsPage.importAndExecute(
                                getBookingMailbox,
                                getBookingId,
                                entryPoint
                            );
                            break;
                        default:
                            lazyOpenBookingPage.importAndExecute(getBookingMailbox, entryPoint);
                            break;
                    }
                } else {
                    lazyOpenBookingPage.importAndExecute(getBookingMailbox, entryPoint);
                }
                break;
            case ORGANIZER_PAGE_KEYWORD:
                lazySetOrganizerEntryPoint.importAndExecute(entryPoint, redirectFromOldUrl);
                lazySetShowServiceOutageBanner.importAndExecute(true);

                if (urlPathname.length > 3 && urlPathname[3] == MEETING_TYPE_ROUTE_KEYWORD) {
                    const meetingTypeId = urlPathname[4];
                    if (meetingTypeId == ORGANIZER_NEW_MEETING_TYPE_ROUTE_KEYWORD) {
                        if (
                            hasQueryStringParameter('title') ||
                            hasQueryStringParameter('duration')
                        ) {
                            lazySetOrganizerMeetingDetails.importAndExecute(
                                meetingTitle,
                                meetingDuration,
                                entryPoint
                            );
                        }
                        lazyOpenBookingCompose.importAndExecute(null, entryPoint);
                    } else {
                        lazyEditBookingMeeting.importAndExecute(meetingTypeId, null, entryPoint);
                    }
                } else {
                    lazyOpenBookingDashboard.importAndExecute(null, entryPoint);
                }
                break;
            default:
                lazySetOrganizerEntryPoint.importAndExecute(entryPoint, 0);
                lazyOpenBookingDashboard.importAndExecute(null, entryPoint);
                break;
        }
    }

    return {
        view: isAttendeeView ? BookWithMeView.Attendee : BookWithMeView.Organizer,
        getRequestUrl: validatedGetRequestUrl,
        bookingMailbox: getBookingMailbox,
        meetingTypeId: getMeetingTypeId,
        bookingId: getBookingId,
        entryPoint,
    };
}

function decodeMailboxIfNeeded(mailbox: string) {
    if (!mailbox || mailbox.length == 0) {
        return mailbox;
    }

    if (mailbox.includes('%2540')) {
        const ENCODED_ATSIGN_REGEX = new RegExp('%2540', 'g');
        const DECODED_ATSIGN = '@';
        return mailbox.replace(ENCODED_ATSIGN_REGEX, DECODED_ATSIGN);
    }
    const ENCODED_ATSIGN_REGEX = new RegExp('%40', 'g');
    const DECODED_ATSIGN = '@';
    return mailbox.replace(ENCODED_ATSIGN_REGEX, DECODED_ATSIGN);
}
